/**
 * Patient Store - Healthcare Patient Management
 *
 * Manages patient data, search results, and patient selection.
 * Provides hooks for patient-related operations and state.
 */

import type { IPatient, PatientStoreState } from "../../../models/patient";
import { createAsyncActions, createStore, withLogging } from "../../core";

import type { BaseStore } from "../../types/core";

// Initial state
const initialState: Omit<PatientStoreState, keyof PatientStoreActions> = {
  currentPatient: null,
  patients: [],
  searchResults: [],
  selectedPatientId: null,
  loading: false,
  error: null,
  _initialized: false,
  _lastUpdated: Date.now(),
};

// Actions interface
interface PatientStoreActions {
  setCurrentPatient: (patient: IPatient | null) => void;
  setCurrentTotalOutstanding: (amount: number) => void;
  setPatients: (patients: IPatient[]) => void;
  setSearchResults: (results: IPatient[]) => void;
  setSelectedPatientId: (id: string | null) => void;
  addPatient: (patient: IPatient) => void;
  updatePatient: (id: string, updates: Partial<IPatient>) => void;
  removePatient: (id: string) => void;
  clearSearchResults: () => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
  reset: () => void;
}

// Complete store state
type CompletePatientStoreState = PatientStoreState & PatientStoreActions;

// Create the store
const usePatientStore = createStore<CompletePatientStoreState>(
  withLogging("PatientStore", (set) => ({
    ...initialState,
    ...createAsyncActions<CompletePatientStoreState>(),

    // Patient actions
    setCurrentPatient: (patient: IPatient | null) =>
      set((state) => {
        state.currentPatient = patient;
      }),

    setCurrentTotalOutstanding: (amount: number) =>
      set((state) => {
        if (state.currentPatient) {
          state.currentPatient = { ...state.currentPatient, totalOutstanding: amount };
        }
      }),

    setPatients: (patients: IPatient[]) =>
      set((state) => {
        state.patients = patients;
      }),

    setSearchResults: (results: IPatient[]) =>
      set((state) => {
        state.searchResults = results;
      }),

    setSelectedPatientId: (id: string | null) =>
      set((state) => {
        state.selectedPatientId = id;
      }),

    addPatient: (patient: IPatient) =>
      set((state) => {
        state.patients.push(patient);
      }),

    updatePatient: (id: string, updates: Partial<IPatient>) =>
      set((state) => {
        const index = state.patients.findIndex((p) => p.id === id);
        if (index !== -1) {
          state.patients[index] = { ...state.patients[index], ...updates };
        }
        if (state.currentPatient?.id === id) {
          // Only apply updates for fields that exist in ISearchPatient
          const updatedPatient = { ...state.currentPatient };
          Object.keys(updates).forEach((key) => {
            if (key in updatedPatient) {
              (updatedPatient as Record<string, unknown>)[key] = (
                updates as Record<string, unknown>
              )[key];
            }
          });
          state.currentPatient = updatedPatient;
        }
      }),

    removePatient: (id: string) =>
      set((state) => {
        state.patients = state.patients.filter((p) => p.id !== id);
        if (state.currentPatient?.id === id) {
          state.currentPatient = null;
        }
      }),

    clearSearchResults: () =>
      set((state) => {
        state.searchResults = [];
      }),

    clearError: () =>
      set((state) => {
        state.error = null;
      }),

    reset: () =>
      set((state) => {
        Object.assign(state, initialState);
        state._lastUpdated = Date.now();
      }),
  })),
  {
    name: "patient-store",
    // Not persisted (ECHO-2318): patient data must not be written to localStorage. The
    // consultation page refetches the patient from the route's patientId on every mount.
    persist: false,
  },
);

// ===== SELECTORS =====

// Data selectors
export const usePatient = () => usePatientStore((state) => state.currentPatient);

// Individual action selectors to prevent infinite loops
export const useSetCurrentPatient = () => usePatientStore((state) => state.setCurrentPatient);

// Legacy combined actions hook - use individual hooks above for better performance
export const usePatientActions = () => ({
  setCurrentPatient: useSetCurrentPatient(),
  setCurrentTotalOutstanding: usePatientStore((state) => state.setCurrentTotalOutstanding),
  reset: usePatientStore((state) => state.reset),
});

// Export the store for direct access if needed
export { usePatientStore };

// ===== LEGACY COMPATIBILITY =====
// For backward compatibility with existing code using ISearchPatient

const defaultLegacyPatient: IPatient = {
  id: "",
  patientFullName: "",
  patientLastName: "",
  sex: "",
  dateOfBirth: "",
  idNumber: "",
  mobileNumberRegionCode: "",
  mobileNumber: "",
  email: "",
  postalCode: "",
  addressLine1: "",
  addressLine2: "",
  unit: "",
  allergyAlerts: "",
  isActive: true,
  isSoftDeleted: false,
  application: "",
  createdBy: "",
  updatedBy: "",
  createdAt: "",
  updatedAt: "",
  allergies: [],
  foodAllergies: [],
  haveG6pdDeficiency: false,
  alerts: "",
  idType: { code: "" },
  totalOutstanding: 0,
  patientType: {
    id: undefined,
    name: "",
    description: "",
    isSoftDeleted: false,
    createdAt: "",
    updatedAt: "",
  },
  drugAllergiesV2: [],
};

// Legacy patient store for backward compatibility
interface LegacyPatientState extends BaseStore {
  patient: IPatient;
  setPatient: (patient: IPatient) => void;
}

const useLegacyPatientStore = createStore<LegacyPatientState>(
  (set) => ({
    patient: defaultLegacyPatient,
    setPatient: (patient: IPatient) =>
      set((state) => {
        state.patient = patient;
      }),
  }),
  {
    name: "LegacyPatientStore",
    // Not persisted (ECHO-2318): patient data must not be written to localStorage.
    persist: false,
  },
);

// Export legacy store for backward compatibility
export { useLegacyPatientStore };
